import React, { useState, useEffect, useRef, useId } from 'react';

import {
  COUNTRY_OPTIONS,
  DEFAULT_COUNTRY,
  callingCodeFor,
  formatAsYouType,
  phoneExampleFor,
  splitPhone,
} from '../../utils/phone';

const COUNTRY_ALIASES = { uk: 'GB', britain: 'GB', england: 'GB', usa: 'US', america: 'US', uae: 'AE' };

/**
 * ONE phone input, for every form in this app that asks for a phone number.
 *
 * It lived inside pages/Customers.js, which meant Signup and onboarding's Company Basics had
 * plain text boxes with a US-only placeholder and no country selector at all — so an
 * international contractor was never told which country was being assumed, and onboarding
 * then displayed the normalised value back as a raw "+15098647357" in another bare input.
 *
 * Moving it rather than writing a third one is the whole point. This control has been broken
 * and repaired three times (a greedy regex that ate two digits of the number, a stateless
 * version that stored every non-US number as +1, and a re-sync that jumped United Kingdom to
 * Guernsey because +44 is shared). Every one of those was fixed here; a copy-paste into
 * Signup would have started that history over from the beginning.
 *
 * Behaviour is unchanged from the Customers version — the body below is that component,
 * moved. tests/e2e/customers/customers-phone-country-code.spec.js locks it, deliberately
 * using +44 because +1 is the fallback and so cannot fail.
 */

// Native <select> popups are drawn by the OS and ignore the app's dark theme unless the
// option rows are told. Same pair the rest of the app uses.
const COUNTRY_BY_ISO_LOCAL = Object.fromEntries(COUNTRY_OPTIONS.map((c) => [c.iso, c]));

const FieldError = ({ children }) => (
  children ? <p className="text-xs text-red-600 dark:text-red-400 mt-1">{children}</p> : null
);

/**
 * `labelClassName` / `inputClassName` exist so a surface with its own visual language can ADOPT
 * this control instead of forking it. Company Profile's cards use a heavier style (py-4,
 * text-lg, bold labels) than the rest of the app; without these two props the only ways to use
 * the shared control there were to accept a visibly foreign field or to write a second phone
 * input — and the comment above is a list of what writing a second one costs. `label` is
 * rendered as a node, so a caller can pass JSX and keep its own icon.
 */
const PhoneField = ({
  label,
  /**
   * A STRING name for assistive technology (board #610, #1179).
   *
   * `label` is allowed to be JSX — several callers pass a node so they can render a required
   * asterisk or help text — and this component interpolated that node straight into
   * aria-label. A React element stringifies to "[object Object]", so the country button
   * announced itself as "[object Object] country" and the phone input as "[object Object]".
   * Filed three times on onboarding and again on Company Profile.
   *
   * Falls back to the label only when it is genuinely a string, and to a sensible default
   * otherwise, so an existing caller that passes plain text needs no change.
   */
  ariaLabel,
  value,
  onChange,
  error,
  testId,
  name = 'phone',
  disabled,
  // #4116 #4628: a caller that marks the label required (Signup) had no way to tell the input,
  // so assistive tech heard an optional field next to a visible asterisk.
  required = false,
  labelClassName ='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1',
  inputClassName = 'flex-1 min-w-0 px-3 py-2 border border-l-0 border-white/20 dark:border-slate-700/40 rounded-r-md bg-white/70 dark:bg-slate-800/60 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500',
  // #4983: the picker button is the other half of the same visual row, so a caller that
  // restyles the input (Signup, hardcoded dark) must restyle this too or the two halves
  // render in different palettes.
  countryButtonClassName = 'flex items-center gap-1 px-3 py-2 h-full w-[9rem] border border-white/20 dark:border-slate-700/40 rounded-l-md bg-white/70 dark:bg-slate-800/60 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500',
  // Passed straight to the number input (#4943: "tel" on Signup).
  autoComplete,
}) => {
  // Split once to seed the picker, then again against the CHOSEN country so a mid-typing or
  // letter-bearing value still gets its calling code stripped off for display.
  const seed = splitPhone(value);

  /**
   * The chosen country has to be STATE, and the first version of this component got that
   * wrong in a way that was worse than the bug it replaced.
   *
   * It derived everything from the stored value and kept no state at all. On a new customer
   * the stored value is empty, so picking +44 composed '' — a bare country code is not a
   * phone number worth saving — which threw the choice away; the component re-derived from
   * the still-empty value and the picker snapped back to +1. Typing the number next composed
   * it with +1.
   *
   * Net effect: every non-US number was silently stored as a US one. +44 7911 123456 became
   * +17911123456, which is 11 digits starting with 1, so the list formatted it as
   * (791) 112-3456 and it looked entirely plausible. The original greedy-regex bug at least
   * produced visibly mangled output; this one produced confident, wrong data.
   *
   * The country therefore lives here, seeded from the stored value and re-synced only when an
   * incoming value actually resolves to one. `nationalNumber` is never mirrored, so the drift
   * the stateless version was guarding against still cannot happen.
   */
  const [chosenCountry, setChosenCountry] = useState(seed.country || DEFAULT_COUNTRY);
  const inputId = `phone-${useId().replace(/:/g, '')}`;

  useEffect(() => {
    // Only follow a value that actually PARSES to a country. An empty field, or a half-typed
    // one, must NOT reset the user's choice — that reset is the entire defect above.
    //
    // And only when the CALLING CODE actually differs. Countries share calling codes: +44 is
    // the UK, Guernsey, Jersey and the Isle of Man; +1 is the US, Canada and nineteen others.
    // libphonenumber resolves a number to ONE of them by range, so picking United Kingdom and
    // typing 7911123456 parses back as GG — and re-syncing on country alone made the picker
    // visibly jump to Guernsey while the user was typing. Caught in a browser, not by
    // reading this code. If the calling code matches, the user's choice is not wrong and
    // nothing needs correcting; if it differs, the value genuinely belongs elsewhere (someone
    // pasted a full +49 number into a US-set field) and following it is right.
    if (!value) return;
    const parsedCountry = splitPhone(value).country;
    if (!parsedCountry || parsedCountry === chosenCountry) return;
    if (callingCodeFor(parsedCountry) === callingCodeFor(chosenCountry)) return;
    setChosenCountry(parsedCountry);
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedCountry = chosenCountry;
  const selectedCode = callingCodeFor(selectedCountry);
  const { nationalNumber } = splitPhone(value, selectedCountry);

  // Drops FORMATTING only — spaces, parens, dots, dashes that as-you-type inserts.
  //
  // Letters used to be carried through here on purpose, so the "cannot contain letters" check
  // had something to fire on. Operator ruling 2026-09-24 (board #1619) replaced that: a phone
  // field should not accept a letter in the first place. They are now filtered at the input's
  // onChange, below, so nothing reaches compose(). The live check is kept for values that
  // arrive from elsewhere (legacy rows, programmatic sets).
  const compose = (nextCountry, nextNational) => {
    const raw = String(nextNational);

    // Board #1967. A LEADING + means the user is stating their own country code, so honour it
    // instead of stripping it and prefixing the picker's. This used to strip the + and treat the
    // rest as a national number, so pasting "+447911123456" into a field set to +1 produced
    // "+1447911123456" — a number that belongs to nobody, built by silently reinterpreting the
    // country the user had just typed. The useEffect above then follows the parsed country, so
    // the picker moves to match what they entered.
    if (raw.trim().startsWith('+')) {
      const intl = raw.replace(/[()\s.\-]/g, '');
      return intl === '+' ? '' : intl;
    }

    const cleaned = raw.replace(/[()\s.\-+]/g, '');
    return cleaned === '' ? '' : `${callingCodeFor(nextCountry)}${cleaned}`;
  };

  // A string, never a React node — see the ariaLabel prop.
  const fieldLabel = ariaLabel || (typeof label === 'string' ? label : 'Phone number');

  const [countryOpen, setCountryOpen] = useState(false);
  const [countryQuery, setCountryQuery] = useState('');
  const countryWrapRef = useRef(null);
  // #4954: the list carried listbox/option roles but the only way to pick a row was the mouse.
  // Arrow keys move this index over the FILTERED list, Enter chooses it, Home/End jump; it
  // resets to the top whenever the query changes so the highlight never points at a row that
  // just filtered out. Mouse behaviour is untouched.
  const [activeIndex, setActiveIndex] = useState(0);
  const listboxId = `${inputId}-countries`;

  // Board #1242. The country list had no way out except choosing a country or clicking the
  // trigger again — clicking anywhere else on the form left a full country list sitting over
  // the fields underneath. Click-away and Escape are what every other popup in this app does
  // (the price-book category combobox included), and what a user tries first.
  useEffect(() => {
    if (!countryOpen) return undefined;
    const onPointerDown = (e) => {
      // Interactions INSIDE the control — the search box, the list — must not close it.
      if (countryWrapRef.current && !countryWrapRef.current.contains(e.target)) {
        setCountryOpen(false);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setCountryOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [countryOpen]);

  // Name, ISO and calling code are all searchable because all three are things people know
  // a country by — "United Kingdom", "GB", "+44".
  // #5745: common short names that are not the ISO code ("UK" is GB; it used to match
  // "Ukraine" by name and Enter picked it). Matches are ranked so an exact code or alias comes
  // first, then names that start with the query, then everything else.
  const filteredCountries = (() => {
    const q = countryQuery.trim().toLowerCase().replace(/^\+/, '');
    if (!q) return COUNTRY_OPTIONS;
    const aliasIso = COUNTRY_ALIASES[q];
    const rank = (c) => {
      const iso = c.iso.toLowerCase();
      const name = c.name.toLowerCase();
      if (iso === q || (aliasIso && c.iso === aliasIso)) return 0;
      if (name.startsWith(q)) return 1;
      return 2;
    };
    return COUNTRY_OPTIONS.filter((c) =>
      c.name.toLowerCase().includes(q)
      || c.iso.toLowerCase().startsWith(q)
      || c.callingCode.replace('+', '').startsWith(q)
      || (aliasIso && c.iso === aliasIso)
    ).map((c, i) => ({ c, i, r: rank(c) }))
      .sort((a, b) => a.r - b.r || a.i - b.i)
      .map(({ c }) => c);
  })();

  const handleCountryChange = (nextCountry) => {
    setChosenCountry(nextCountry);
    onChange(compose(nextCountry, nationalNumber));
  };

  const activeCountry = filteredCountries[activeIndex];

  const onSearchKeyDown = (e) => {
    const last = filteredCountries.length - 1;
    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        setCountryOpen(false);
        break;
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex((i) => (last < 0 ? 0 : Math.min(i + 1, last)));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
        break;
      case 'Home':
        e.preventDefault();
        setActiveIndex(0);
        break;
      case 'End':
        e.preventDefault();
        setActiveIndex(last < 0 ? 0 : last);
        break;
      case 'Enter':
        e.preventDefault();
        if (activeCountry) {
          handleCountryChange(activeCountry.iso);
          setCountryOpen(false);
        }
        break;
      default:
        break;
    }
  };

  // Keep the highlighted row in view as the arrow keys move it through the scrolling list.
  useEffect(() => {
    if (!countryOpen || !countryWrapRef.current) return;
    const el = countryWrapRef.current.querySelector('[data-active-option="true"]');
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [countryOpen, activeIndex]);

  const liveError = /[a-zA-Z]/.test(nationalNumber)
    ? 'A phone number cannot contain letters.'
    : '';

  // Formatted for reading while typing ("(541) 705-0524"), but only once it parses cleanly —
  // never re-format a value carrying letters, or the offending characters vanish from the box
  // and the error points at something the user can no longer see.
  const displayedNational = liveError
    ? nationalNumber
    : formatAsYouType(nationalNumber, selectedCountry);

  return (
    <div>
      {/* #3445: the visible label is tied to the number input, so it is programmatically
          associated and clicking it focuses the field. aria-label on the input still names it
          (and the country button) for callers that pass JSX or an empty label. */}
      <label className={labelClassName} htmlFor={inputId}>
        {label}
      </label>
      {/* min-w-0 on both the row and the text box. A flex child will not shrink below its
          content by default, so in a narrow grid cell — the Edit Customer modal's two-column
          row — the picker and the number pushed past their container and sat on top of the
          Status field beside them. min-w-0 lets them give way; the picker keeps its width
          with shrink-0 so the calling code stays readable while the number narrows. */}
      <div className="flex min-w-0">
        {/* SEARCHABLE, because the list is ~250 long since it came from libphonenumber.
            A native select gives you one keystroke of type-ahead against the option TEXT,
            which here begins with a flag and a calling code — so typing "u" for United
            Kingdom matched nothing useful and the only way down the list was to scroll it.
            This filters on country name, ISO code and calling code, so "United", "GB" and
            "+44" all find the same row. Storage is unchanged: the value is still the ISO
            country and the number is still composed to E.164. */}
        <div className="relative shrink-0" ref={countryWrapRef}>
          <button
            type="button"
            disabled={disabled}
            onClick={() => {
              setCountryOpen((v) => !v);
              setCountryQuery('');
              // Open on the chosen country, not row 0: the highlighted row carries
              // aria-selected, so starting at Afghanistan announced the wrong selection (#5559).
              setActiveIndex(Math.max(0, COUNTRY_OPTIONS.findIndex((c) => c.iso === selectedCountry)));
            }}
            aria-haspopup="listbox"
            aria-expanded={countryOpen}
            aria-label={`${fieldLabel} country`}
            data-testid={`${testId}-country`}
            className={countryButtonClassName}
          >
            <span>{COUNTRY_BY_ISO_LOCAL[selectedCountry]?.flag || ''}</span>
            <span className="truncate">{selectedCode}</span>
            <span className="ml-auto text-xs opacity-60">▾</span>
          </button>
          {countryOpen && (
            <div className="absolute z-50 mt-1 w-72 max-h-64 overflow-y-auto rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-xl">
              <input
                autoFocus
                value={countryQuery}
                onChange={(e) => { setCountryQuery(e.target.value); setActiveIndex(0); }}
                onKeyDown={onSearchKeyDown}
                placeholder="Search country, code or +44"
                aria-label="Search countries"
                role="combobox"
                aria-expanded="true"
                aria-controls={listboxId}
                aria-autocomplete="list"
                aria-activedescendant={activeCountry ? `${listboxId}-${activeCountry.iso}` : undefined}
                className="w-full px-3 py-2 border-b border-slate-200 dark:border-slate-700 bg-transparent text-sm text-gray-900 dark:text-white focus:outline-none"
              />
              <ul id={listboxId} role="listbox" aria-label="Countries">
                {filteredCountries.length === 0 && (
                  <li className="px-3 py-2 text-sm text-gray-500">No country matches that.</li>
                )}
                {filteredCountries.map((c, idx) => (
                  <li key={c.iso}>
                    <button
                      type="button"
                      role="option"
                      id={`${listboxId}-${c.iso}`}
                      data-active-option={idx === activeIndex ? 'true' : undefined}
                      aria-selected={idx === activeIndex}
                      aria-current={c.iso === selectedCountry ? 'true' : undefined}
                      tabIndex={-1}
                      onMouseEnter={() => setActiveIndex(idx)}
                      onClick={() => { handleCountryChange(c.iso); setCountryOpen(false); }}
                      className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                        idx === activeIndex || c.iso === selectedCountry
                          ? 'bg-slate-100 dark:bg-slate-700 text-gray-900 dark:text-white'
                          : 'text-gray-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700'
                      }`}
                    >
                      {c.flag} {c.name} <span className="opacity-60">{c.callingCode}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        {/* Board #1361: the placeholder was hardcoded "555 123 4567" — a US number — beside a
            picker offering ~240 countries, so choosing +44 left a US-shaped example in the box
            telling the user what to type. It now comes from the selected country's own
            metadata, which is what intl-tel-input does, and falls back to the generic words
            rather than to a US number: a wrong example is worse than none. */}
        <input
          id={inputId}
          name={name}
          aria-label={fieldLabel}
          aria-invalid={Boolean(error || liveError)}
          type="tel"
          inputMode="tel"
          autoComplete={autoComplete}
          // #2102: no cap meant 260 characters went in. A national number is at most 15 digits
          // (E.164) and formatting adds a few characters; 25 leaves room for an extension.
          maxLength={25}
          required={required}
          disabled={disabled}
          value={displayedNational}
          // Board #1619 / operator ruling 2026-09-24: keep digits and the ordinary phone
          // formatting characters, refuse letters and everything else on typing AND on paste.
          // Dropping the character leaves the controlled value unchanged, so the caret stays
          // where the user left it.
          onChange={(e) => onChange(compose(selectedCountry, e.target.value.replace(/[^0-9+()\-. ]/g, '')))}
          placeholder={phoneExampleFor(selectedCountry) || 'Phone number'}
          data-testid={testId}
          className={inputClassName}
        />
      </div>
      {/* Letters are flagged AS YOU TYPE, not on submit. The submit-time check alone left
          "abcdefg" sitting in the box looking accepted, with the button still enabled, and
          the failure only arriving after you pressed it. Restricted to letters on purpose:
          a full plausibility check while typing would mark every half-typed number invalid
          ("5", "541", "5417") and train the user to ignore it. Letters are never ambiguous
          — format_phone_e164 discards the value outright — so they can be called
          immediately, and the complete check still runs at submit. */}
      <FieldError>{error || liveError}</FieldError>
      {/* Board #1967: the hint described a save that was not going to happen. It rendered
          whenever there was no error, including on an EMPTY field — so clearing the box left
          "Saved as +1 followed by the number" underneath it, stating a fact about nothing. */}
      {!error && !liveError && displayedNational.trim() !== '' && (
        <p className="text-xs text-gray-500 mt-1">
          Saved as {selectedCode} followed by the number.
        </p>
      )}
    </div>
  );
};

export default PhoneField;
